<script lang="ts">
  export type NotificationType = 'error' | 'success' | 'info' | 'warning';

  let {
    type = 'info',
    message = '',
    onDismiss = undefined as (() => void) | undefined,
    autoDismiss = false,
    autoDismissDelay = 3000
  }: {
    type?: NotificationType;
    message: string;
    onDismiss?: () => void;
    autoDismiss?: boolean;
    autoDismissDelay?: number;
  } = $props();

  let timeoutId: number | undefined = $state(undefined);

  // Auto-dismiss functionality
  $effect(() => {
    if (autoDismiss && onDismiss) {
      timeoutId = window.setTimeout(() => {
        onDismiss();
      }, autoDismissDelay);

      return () => {
        if (timeoutId) clearTimeout(timeoutId);
      };
    }
  });
</script>

<div class="notification" class:error={type === 'error'} class:success={type === 'success'} class:info={type === 'info'} class:warning={type === 'warning'}>
  <span class="message">{message}</span>
  {#if onDismiss}
    <button class="dismiss-btn" onclick={onDismiss} aria-label="Dismiss notification">
      ×
    </button>
  {/if}
</div>

<style lang="scss">
  .notification {
    padding: var(--spacing-md) var(--spacing-lg);
    border-radius: var(--radius-lg);
    margin-bottom: 16px;
    font-size: var(--font-size-sm);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-md);
    animation: slideIn 0.2s ease-out;

    &.error {
      background: var(--color-danger-light);
      color: var(--color-danger-dark);
      border: 1px solid var(--color-danger-border);
    }

    &.success {
      background: var(--color-success-light);
      color: var(--color-success-dark);
      border: 1px solid var(--color-success-border);
    }

    &.info {
      background: var(--color-primary-light);
      color: var(--color-primary-dark);
      border: 1px solid var(--color-primary-border);
    }

    &.warning {
      background: var(--color-warning-light);
      color: var(--color-warning-dark);
      border: 1px solid var(--color-warning-border);
    }

    .message {
      flex: 1;
      font-weight: var(--font-weight-medium);
    }

    .dismiss-btn {
      background: transparent;
      border: none;
      color: currentColor;
      font-size: var(--font-size-2xl);
      line-height: var(--line-height-none);
      cursor: pointer;
      padding: 0 8px;
      opacity: 0.7;
      transition: opacity 0.15s;

      &:hover {
        opacity: 1;
      }
    }
  }

  @keyframes slideIn {
    from {
      transform: translateY(-10px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
</style>
